import posthog from 'posthog-js';
import {
  disablePostHogExceptionCapture,
  pausePostHogExceptionCapture,
  startPostHogExceptionCapture,
} from '@/components/entities/analytics/components/PostHog/utils/postHogExceptionCapture';
import { Env } from '@/consts/Env';

const posthogKey = Env.NEXT_PUBLIC_POSTHOG_KEY;
const posthogHost = Env.NEXT_PUBLIC_POSTHOG_HOSTNAME;
const posthogRelease = Env.NEXT_PUBLIC_APP_RELEASE;
const posthogReleaseProperties = {
  app_environment: Env.NEXT_PUBLIC_ENVIRONMENT,
  ...(posthogRelease ? { app_release: posthogRelease } : {}),
};

if (posthogKey && posthogHost) {
  let hasInitializedPosthog = false;

  const initializePosthog = () => {
    if (hasInitializedPosthog) {
      posthog.opt_in_capturing();
      posthog.startSessionRecording(true);
      startPostHogExceptionCapture();
      return;
    }

    // Check if consent for statistics cookies is granted on Cookiebot.
    const consentState = window?.Cookiebot?.consent;

    // Keep PostHog blocked until consent for statistics cookies is granted.
    if (!consentState?.statistics) {
      return;
    }

    // Use relay for production, direct URLs for localhost development
    // const IS_LOCALHOST = typeof window !== 'undefined' && window.location.hostname === 'localhost';

    // const RELAY_PATH = Env.NEXT_PUBLIC_POSTHOG_RELAY_PATH;

    posthog.init(posthogKey, {
      // api_host: IS_LOCALHOST ? HOST : RELAY_PATH,
      api_host: posthogHost,
      ui_host: posthogHost,
      defaults: '2025-05-24',
      person_profiles: 'identified_only',
      // advanced_disable_decide: false, // This disables PostHog's Decide API, which is responsible for feature flags and A/B testing. Set to true if you don't use these features or want to manage them server-side.
      advanced_disable_flags: false,
      enable_heatmaps: true,
      disable_session_recording: false,
      autocapture: true,
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        // Console errors are usually too noisy to be actionable.
        capture_console_errors: false,
      },
      error_tracking: {
        exception_steps: {
          enabled: true,
          max_bytes: 32_768,
        },
      },
      debug: Env.NODE_ENV === 'development',
      session_recording: {
        maskAllInputs: true,
        recordCrossOriginIframes: false,
        compress_events: true,
      },
    });
    hasInitializedPosthog = true;
    posthog.register(posthogReleaseProperties);
    posthog.addExceptionStep('posthog_initialized', posthogReleaseProperties);
    posthog.startSessionRecording(true);
    startPostHogExceptionCapture();
  };

  if (typeof window !== 'undefined') {
    const syncPosthogWithConsent = () => {
      if (window.Cookiebot?.consent?.statistics) {
        initializePosthog();
        return;
      }

      pausePostHogExceptionCapture();
      if (hasInitializedPosthog) {
        posthog.stopSessionRecording();
        posthog.opt_out_capturing();
      }
    };

    window.addEventListener('CookiebotOnLoad', syncPosthogWithConsent);
    window.addEventListener('CookiebotOnAccept', syncPosthogWithConsent);
    window.addEventListener('CookiebotOnDecline', syncPosthogWithConsent);
    window.addEventListener('CookieConsentDeclaration', syncPosthogWithConsent);

    // `instrumentation-client` runs before the app becomes interactive. Initialize
    // immediately when a returning visitor already has statistics consent.
    syncPosthogWithConsent();
  }
} else {
  disablePostHogExceptionCapture();
  console.warn('[PostHog] Key or hostname is not set. PostHog will not initialize.');
}
